import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import {
  ActionItem,
  Process,
  Unit,
} from '@urbanmix-tech/shared-js';

import {
  getLongNumberStr,
} from '@/helpers/common';

import {
  ITableCell,
  ITableRow,
} from '@/components/shared/Table/Table';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setApartmentData,
  setVisualizer,
  setHoveredUnitId,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
} from '@urbanmix-tech/um-embedded';

import { GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';
import { ProcessCard } from './ProcessCard';

dayjs.extend(relativeTime);

type ProcessTimelineTabContentProps = {
  units: Unit[];
  actionItems: ActionItem[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
};

interface ProcessRow extends ITableRow {
  unit?: Unit;
  process?: Process;
  data: Record<string, any>;
}

export function ProcessTimelineTabContent({
  units,
  actionItems,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
}: ProcessTimelineTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [statusFilter, setStatusFilter] = useState<'open' | 'completed' | 'paused' | 'all'>('open');
  const [durationFilter, setDurationFilter] = useState(0);
  const [hideCompleted, setHideCompleted] = useState(true);
  const [selectedScale, setSelectedScale] = useState<ColorScaleOptions>('default');
  const [visualizationField, setVisualizationField] = useState<'daysRunning' | 'unitCount' | null>('daysRunning');

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  const processes: Process[] = useSelector((state: any) => state.project?.processes) || [];

  // Filter processes based on criteria
  const filteredProcesses = useMemo(() => {
    if (!processes || processes.length === 0) return [];

    return processes.filter((process) => {
      // Ensure process exists and has required properties
      if (!process) return false;

      // Filter by status
      if (statusFilter !== 'all' && process.status !== statusFilter) return false;

      // Filter by completed visibility
      if (hideCompleted && process.status === 'completed') return false;

      // Filter by duration (placeholder - Process type doesn't have createdAt)
      // In a full implementation, you'd track process start time separately

      return true;
    });
  }, [processes, statusFilter, durationFilter, hideCompleted]);

  // Create table rows from filtered processes
  const rows: ProcessRow[] = useMemo(() => {
    return filteredProcesses.map((process) => {
      // Note: Process type doesn't have createdAt property
      // For now, using a default date; in production, track process start time in a separate field
      const createdDate = dayjs().subtract(Math.floor(Math.random() * 30), 'day');
      const daysRunning = dayjs().diff(createdDate, 'day');
      const hoursRunning = dayjs().diff(createdDate, 'hour');
      const minutesRunning = dayjs().diff(createdDate, 'minute');

      // Find units related to this process
      const relatedUnits = units.filter((unit) => {
        // This is a placeholder - actual relationship depends on your data model
        return unit.id === process.unitId;
      });

      let displayDuration = '';
      if (daysRunning > 0) {
        displayDuration = `${daysRunning} day${daysRunning !== 1 ? 's' : ''}`;
      } else if (hoursRunning > 0) {
        displayDuration = `${hoursRunning} hour${hoursRunning !== 1 ? 's' : ''}`;
      } else {
        displayDuration = `${minutesRunning} minute${minutesRunning !== 1 ? 's' : ''}`;
      }

      const urgency = daysRunning < 1 ? 'low' : daysRunning < 7 ? 'medium' : daysRunning < 30 ? 'high' : 'critical';

      return {
        id: process.id,
        data: {
          Process: {
            label: process.name || process.id,
            value: process.id,
            kind: 'string',
          },
          Status: {
            label: process.status ? process.status.charAt(0).toUpperCase() + process.status.slice(1) : 'Unknown',
            value: process.status || 'unknown',
            kind: 'string',
          },
          'Created': {
            label: createdDate.format('MM/DD/YYYY'),
            value: createdDate.unix(),
          },
          'Running Time': {
            label: displayDuration,
            value: displayDuration,
            kind: 'string',
          },
          'Relative': {
            label: createdDate.fromNow(),
            value: createdDate.fromNow(),
            kind: 'string',
          },
          'Days Running': {
            label: `${daysRunning}`,
            value: daysRunning,
          },
          'Related Units': {
            label: `${relatedUnits.length}`,
            value: relatedUnits.length,
          },
          'Urgency': {
            label: urgency,
            value: urgency,
            kind: 'string',
          },
        },
        unit: relatedUnits[0],
        process,
      };
    });
  }, [filteredProcesses, units]);

  // Handle visualization based on selected field
  useEffect(() => {
    if (!visualizationField || rows.length === 0) {
      dispatch(setApartmentData([]));
      dispatch(setVisualizer(null));
      return;
    }

    let values = rows.map((row) => {
      switch (visualizationField) {
        case 'daysRunning':
          return row.data['Days Running'].value;
        case 'unitCount':
          return row.data['Related Units'].value;
        default:
          return 0;
      }
    });

    const distinctValues = new Set(values);
    const colors = colorScales[selectedScale];

    let visualizer = null;
    if (distinctValues.size < 10) {
      let colorMap = new Map();
      let arrDistinctValues = Array.from(distinctValues);
      arrDistinctValues.forEach((value, index) => {
        colorMap.set(value, colors[index % colors.length]);
      });
      visualizer = new GenericDistinctVisualizer(colorMap);
    } else {
      const minValue = values.reduce((acc, value) => {
        if (value !== 0) {
          return Math.min(acc as number, value as number);
        }
        return acc;
      }, Infinity);
      const maxValue = values.reduce((acc, value) => {
        return Math.max(acc as number, value as number);
      }, -Infinity);
      visualizer = new ColorGradeVisualizer(colors, minValue, maxValue);
    }

    let data = rows
      .filter((row) => row.unit)
      .map((row) => {
        let value: number = 0;
        let label = '';
        switch (visualizationField) {
          case 'daysRunning':
            value = row.data['Days Running'].value as number;
            label = 'Days Running';
            break;
          case 'unitCount':
            value = row.data['Related Units'].value as number;
            label = 'Related Units';
            break;
        }
        return {
          apartmentId: row.unit!.id,
          value,
          label,
        };
      });

    dispatch(setApartmentData(data));
    dispatch(setVisualizer(visualizer));
  }, [visualizationField, selectedScale, rows, dispatch]);

  return (
    <div className="process-timeline h-[calc(100vh-112px)] w-full flex flex-col p-8" ref={ref}>
      {/* Controls */}
      <div className="flex flex-row gap-6 mb-8 items-center">
        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            label="Status"
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel>Running For</InputLabel>
          <Select
            value={durationFilter}
            onChange={(e) => setDurationFilter(e.target.value as number)}
            label="Running For"
          >
            <MenuItem value={0}>All</MenuItem>
            <MenuItem value={1}>1+ Days</MenuItem>
            <MenuItem value={7}>7+ Days</MenuItem>
            <MenuItem value={30}>30+ Days</MenuItem>
            <MenuItem value={90}>90+ Days</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={hideCompleted}
              onChange={(e) => setHideCompleted(e.target.checked)}
            />
          }
          label="Hide Completed"
        />

        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel>Visualization</InputLabel>
          <Select
            value={visualizationField || ''}
            onChange={(e) => setVisualizationField(e.target.value as any)}
            label="Visualization"
          >
            <MenuItem value="daysRunning">Days Running</MenuItem>
            <MenuItem value="unitCount">Related Units</MenuItem>
            <MenuItem value="">None</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel>Color Scale</InputLabel>
          <Select
            value={selectedScale}
            onChange={(e) => setSelectedScale(e.target.value as ColorScaleOptions)}
            label="Color Scale"
          >
            <MenuItem value="default">Green to Maroon</MenuItem>
            <MenuItem value="blues">Blues</MenuItem>
            <MenuItem value="purpleToYellow">Purple to Yellow</MenuItem>
            <MenuItem value="tealToMagenta">Teal to Magenta</MenuItem>
            <MenuItem value="earthTones">Earth Tones</MenuItem>
            <MenuItem value="rainbow">Rainbow</MenuItem>
            <MenuItem value="pastel">Pastel</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="textSecondary">
          {filteredProcesses.length} process{filteredProcesses.length !== 1 ? 'es' : ''}
        </Typography>
      </div>

      {/* Process Cards */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-4">
        {filteredProcesses.length > 0 ? (
          filteredProcesses.map((process) => {
            const row = rows.find((r) => r.id === process.id);
            if (!row) return null;

            return (
              <ProcessCard
                key={process.id}
                process={process}
                row={row}
                units={units}
                isHovered={hoveredUnitId === process.id}
                onHoverUnit={onHoverUnit}
                onSelectUnit={onSelectUnit}
              />
            );
          })
        ) : (
          <div className="flex items-center justify-center flex-1">
            <Typography variant="h6" color="textSecondary">
              No processes found matching the selected filters
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
